import { SUPPORTED_EXCHANGES } from "../constants/supported-cex";
import { DecimalSchema } from "../decimal";
import { z } from "zod";

export const referralSettingsOverrideSchema = z.object({
  reward: z
    .object({
      splitRatio: z.number().optional(),
    })
    .optional(),
  referrerActivation: z
    .object({
      unlockReward: z
        .object({
          minWeeklyVolumeUsd: z.number().optional(),
        })
        .optional(),
    })
    .optional(),
});
export type ReferralSettingsOverride = z.infer<typeof referralSettingsOverrideSchema>;

export const ReferralCodeSchema = z.object({
  id: z.number(),
  referralCode: z.string(),
  hubUserId: z.number(),
  expiresAt: z.coerce.date().nullable(),
  referralSettingsOverride: referralSettingsOverrideSchema.nullable(),
});
export type ReferralCode = z.infer<typeof ReferralCodeSchema>;

export const ReferralCodeResponseSchema = ReferralCodeSchema.nullable();
export type ReferralCodeResponseDto = z.infer<typeof ReferralCodeResponseSchema>;

export const REFERRAL_STATUS = {
  PENDING: "PENDING",
  ACTIVE: "ACTIVE",
  EXPIRED: "EXPIRED",
  BANNED: "BANNED",
  EXPIRED_AFTER_ACTIVATION: "EXPIRED_AFTER_ACTIVATION",
} as const;
export type REFERRAL_STATUS = (typeof REFERRAL_STATUS)[keyof typeof REFERRAL_STATUS];

export const referralSchema = z.object({
  id: z.number(),
  referrerId: z.number(),
  refereeId: z.number(),
  status: z.nativeEnum(REFERRAL_STATUS),
  createdAt: z.coerce.date(),
  activatedAt: z.coerce.date().nullable(),
  expiredAt: z.coerce.date().nullable(),
  referralCodeId: z.number(),
});
export type Referral = z.infer<typeof referralSchema>;

const referralWithRefereeSchema = referralSchema.extend({
  referee: z.object({
    id: z.number(),
    defiAddress: z.string(),
  }),
  referrer: z
    .object({
      id: z.number(),
      defiAddress: z.string(),
    })
    .optional(),
});
export type ReferralWithReferee = z.infer<typeof referralWithRefereeSchema>;

export const ReferralEligibilityTargetAndCurrentSchema = z.object({
  target: z.number(),
  current: z.number(),
});
export type ReferralEligibilityTargetAndCurrent = z.infer<typeof ReferralEligibilityTargetAndCurrentSchema>;

export const ReferralEligibilityTargetAndCurrentDecimalSchema = z.object({
  target: DecimalSchema,
  current: DecimalSchema,
});
export type ReferralEligibilityTargetAndCurrentDecimal = z.infer<
  typeof ReferralEligibilityTargetAndCurrentDecimalSchema
>;

export const ReferrerEligibilityProgressSchema = z.object({
  exchangeAccountCount: ReferralEligibilityTargetAndCurrentSchema,
  tradeVolumeUSD: ReferralEligibilityTargetAndCurrentDecimalSchema,
  eligibleOrderCount: ReferralEligibilityTargetAndCurrentSchema,
});
export type ReferrerEligibilityProgress = z.infer<typeof ReferrerEligibilityProgressSchema>;

export const RefereeEligibilityProgressSchema = z.object({
  tradeVolumeUSD: ReferralEligibilityTargetAndCurrentDecimalSchema,
  exchangeAccountCount: ReferralEligibilityTargetAndCurrentSchema,
});
export type RefereeEligibilityProgress = z.infer<typeof RefereeEligibilityProgressSchema>;

export const ReferralEpochRewardSchema = z.object({
  volumeUSD: DecimalSchema,
  rewardTokenAmount: z.number(),
});
export type ReferralEpochReward = z.infer<typeof ReferralEpochRewardSchema>;

export const ReferralWithRewardsSchema = referralWithRefereeSchema.extend({
  referralEpochReward: ReferralEpochRewardSchema.array(),
  totalVolumeUSD: DecimalSchema,
  totalRewardTokenAmount: z.string(),
  breakdownByExchange: z
    .record(
      z.string(),
      z.object({
        volumeUSD: z.string(),
        rewardTokenAmount: z.string(),
        /**
         * In-flight referee bot volume on this exchange that is not yet folded
         * into `volumeUSD`/`rewardTokenAmount` (i.e. has not been persisted by
         * the nightly `ReferralEpochBuilder`). `"0"` when there is none.
         * Always `"0"` on the referee-side payload — only the referrer rows
         * carry preview data.
         */
        previewVolumeUSD: z.string(),
      }),
    )
    .optional(),
  bonusExpiresAt: z.coerce.date().nullable().optional(),
});
export type ReferralWithRewards = z.infer<typeof ReferralWithRewardsSchema>;

/**
 * Referrer-side variant: includes the in-flight (not-yet-finalized) bot volume
 * this referee has generated for the referrer during the current epoch.
 * Referee-side payloads stay on the base `ReferralWithRewardsSchema` — the
 * referee already sees their own trading activity elsewhere and doesn't need
 * a per-row preview.
 *
 * Invariant: `UserReferrals.previewVolumeUSD === sum(referralsAsReferrer[].previewVolumeUSD)`.
 */
export const ReferrerReferralWithRewardsSchema = ReferralWithRewardsSchema.extend({
  previewVolumeUSD: z.number(),
});
export type ReferrerReferralWithRewards = z.infer<typeof ReferrerReferralWithRewardsSchema>;

export const UserReferralsSchema = z.object({
  referralsAsReferrer: ReferrerReferralWithRewardsSchema.array(),
  referralAsReferee: ReferralWithRewardsSchema.nullable().optional(),
  /**
   * Aggregate in-flight bot volume for the current epoch across every referee
   * of this referrer. Equal to the sum of `referralsAsReferrer[].previewVolumeUSD`.
   * Always 0 when the user has no referrals.
   */
  previewVolumeUSD: z.number(),
  /**
   * True while the daily `ReferralEpochBuilder` cron is executing. The frontend
   * uses this to show a "computing…" state instead of a (temporarily inconsistent)
   * preview.
   */
  isComputing: z.boolean(),
  /**
   * ISO timestamp of the current epoch's start (UTC midnight of today).
   */
  epochStart: z.string(),
  /**
   * ISO timestamp of when the daily builder cron will finalize the current
   * epoch (~`tomorrow 00:01 UTC`).
   */
  nextEpochRunAt: z.string(),
});
export type UserReferrals = z.infer<typeof UserReferralsSchema>;

/**
 * It contains all the stats of a user as a referrer
 */
export const ReferrerStatsSchema = z.object({
  /**
   * Total rewards claimed by the user as a referrer
   */
  totalRewardsClaimed: z.number(),
  /**
   * Total rewards unclaimed by the user as a referrer
   */
  totalRewardsUnclaimed: z.number(),
  /**
   * Total rewards claimable soon by the user as a referrer
   */
  totalRewardsClaimableSoon: z.number(),
  /**
   * Total number of referrals associated to the user as a referrer
   */
  totalReferralsAsReferrer: z.number(),
  /**
   * Cumulative USD volume traded by every referee of this referrer across all
   * persisted referral epoch rewards. Sourced from `ReferralEpochReward` so it
   * reflects all exchanges currently present in the epoch breakdown (including
   * exchanges that don't grant rewards but are tracked for volume only).
   */
  totalVolumeMadeByAllReferees: z.number(),
});
export type ReferrerStats = z.infer<typeof ReferrerStatsSchema>;

/**
 * It contains all the stats of a user as a referee
 */
export const RefereeStatsSchema = z.object({
  /**
   * Total rewards claimed by the user as a referee
   */
  totalRewardsClaimed: z.number(),
  /**
   * Total rewards unclaimed by the user as a referee
   */
  totalRewardsUnclaimed: z.number(),
  /**
   * Total rewards claimable soon by the user as a referee
   */
  totalRewardsClaimableSoon: z.number(),
  /**
   * Total volume traded by the user as a referee
   */
  totalVolumeTraded: z.number(),
});
export type RefereeStats = z.infer<typeof RefereeStatsSchema>;

export const REFERRAL_REWARD_TIER = {
  CLASSIC: "classic",
  AMBASSADOR_BONUS: "ambassador-bonus",
  AMBASSADOR_STANDARD: "ambassador-standard",
} as const;
export type ReferralRewardTier = (typeof REFERRAL_REWARD_TIER)[keyof typeof REFERRAL_REWARD_TIER];

export const AmbassadorEligibilityResponseSchema = z.object({
  isAmbassador: z.boolean(),
  volume: z.object({
    ownUSD: z.number(),
    refereesUSD: z.number(),
    totalUSD: z.number(),
    targetUSD: z.number(),
    progressRatio: z.number().min(0).max(1),
  }),
  // Eligibility is volume-only: hitting the volume target qualifies a user to
  // open an ambassador ticket. The team also handpicks ambassadors directly,
  // which is granted out-of-band (no UI gate).
  isEligible: z.boolean(),
  applyUrl: z.string().url(),
});
export type AmbassadorEligibilityResponse = z.infer<typeof AmbassadorEligibilityResponseSchema>;

export const ReferralSettingsResponseSchema = z.object({
  refereeRewardSplitRatio: z.number(),
  referrerRewardSplitRatio: z.number(),
  /**
   * The share of broker/builder fees distributed to referrals (referee + referrer combined).
   * 1 = all broker fees are distributed, 0 = none.
   */
  totalBrokerFeeShareRatio: z.number(),
  exchangesRewardSplitRatio: z.record(
    z.enum(SUPPORTED_EXCHANGES),
    z
      .object({
        takerFeeRatio: z.number(),
        rebateToBrokerRatio: z.number(),
      })
      .optional(),
  ),
  refereeActivation: z.object({
    minConnectedCexCount: z.number(),
    timeLimitDays: z.number(),
    minTradeVolumeUSD: z.number(),
  }),
  ambassador: z.object({
    /** Broker-fee share applied to ambassador referrals AFTER the bonus window expires (i.e., post-`bonusDurationDays`). Classic referrers use `reward.totalBrokerFeeShareRatio` instead. */
    totalBrokerFeeShareRatio: z.number().min(0).max(1),
    /** Broker-fee share applied to ambassador referrals DURING the bonus window (first `bonusDurationDays` from `max(referral.createdAt, referrer.ambassadorStatusUpdatedAt)`). */
    totalBrokerFeeShareRatioBonus: z.number().min(0).max(1),
    /** Length of the ambassador bonus window in days, measured from `max(referral.createdAt, referrer.ambassadorStatusUpdatedAt)`. */
    bonusDurationDays: z.number().int().positive(),
  }),
  ambassadorEligibility: z.object({
    minTotalVolumeUSD: z.number().positive(),
  }),
});
export type ReferralSettingsResponseDto = z.infer<typeof ReferralSettingsResponseSchema>;

export const RefereeEligibleResponseSchema = z.object({
  eligible: z.boolean(),
});
export type RefereeEligibleResponse = z.infer<typeof RefereeEligibleResponseSchema>;
